/**
 * Publishes privacy-safe storefront outcomes into Shopify Customer Events.
 * The Umami custom pixel subscribes to these events in its consent sandbox.
 * Never add email addresses, names, phone numbers, free text, or other PII here.
 */
(function () {
  'use strict';

  var STORAGE_PREFIX = 'zelal:analytics:v1:';
  var seen = new Set();

  function publish(name, data, dedupeKey) {
    if (!name || !window.Shopify || !window.Shopify.analytics || !window.Shopify.analytics.publish) return;

    if (dedupeKey) {
      try {
        var key = STORAGE_PREFIX + dedupeKey;
        if (window.sessionStorage.getItem(key) === '1') return;
        window.sessionStorage.setItem(key, '1');
      } catch (error) {
        // Storage may be disabled. The event is still safe to publish.
      }
    }

    try {
      var result = window.Shopify.analytics.publish('zelal:' + name, data || {});
      if (result && result.catch) result.catch(function () {});
    } catch (error) {
      // Analytics must never interfere with the shopping experience.
    }
  }

  window.ZelalAnalytics = { publish: publish };

  function cartContext(element) {
    var scope = element.closest('cart-drawer') || document.getElementById('main-cart-footer');
    var node = scope && scope.querySelector('[data-zelal-cart-facts]');
    try { return node ? JSON.parse(node.textContent) : {}; } catch (error) { return {}; }
  }

  function surfaceFor(element) {
    if (element.closest('cart-drawer')) return 'cart_drawer';
    if (element.closest('product-recommendations')) return 'recommendations';
    if (element.closest('predictive-search')) return 'predictive_search';
    if (/\/search$/.test(location.pathname)) return 'search';
    if (/\/blogs\//.test(location.pathname)) return 'article';
    if (/\/cart$/.test(location.pathname)) return 'cart';
    if (/\/collections\//.test(location.pathname)) return 'collection';
    return 'storefront';
  }

  function productLinkFacts(link) {
    var url;
    try { url = new URL(link.href, location.href); } catch (error) { return null; }
    if (url.origin !== location.origin || !/\/products\/[^/]+/.test(url.pathname)) return null;
    var card = link.closest('.card-wrapper, .predictive-search__list-item, .cart-item');
    var list = link.closest('ul, product-recommendations, .grid');
    return {
      product_handle: url.pathname.split('/products/')[1].split('/')[0],
      surface: surfaceFor(link),
      position: card && list ? Array.from(list.querySelectorAll('.card-wrapper, .predictive-search__list-item, .cart-item')).indexOf(card) + 1 : undefined
    };
  }

  document.addEventListener('click', function (event) {
    var target = event.target.closest && event.target;
    if (!target) return;
    var checkout = target.closest('button[name="checkout"], input[name="checkout"], a[href="/checkout"]');
    if (checkout && !checkout.disabled) publish('checkout_clicked', Object.assign(cartContext(checkout), { surface: surfaceFor(checkout) }));
    var link = target.closest('a[href]');
    var facts = link && productLinkFacts(link);
    if (facts) publish('product_selected', facts);
    var media = target.closest('deferred-media button');
    if (media && media.closest('product-info')) publish('product_demo_opened', {
      surface: 'product', product_id: media.closest('product-info').dataset.productId
    });
  }, true);

  // Observe actual exposure, including dynamically rendered recommendations and carts.
  // A 50% visible element for 500 ms qualifies; offscreen/hidden content does not.
  var timers = new WeakMap();
  var observed = new WeakSet();
  var observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      clearTimeout(timers.get(entry.target));
      if (!entry.isIntersecting || entry.intersectionRatio < 0.5) return;
      timers.set(entry.target, setTimeout(function () {
        if (document.visibilityState !== 'visible' || !entry.target.isConnected) return;
        var element = entry.target;
        var name, facts, key;
        if (element.matches('.product-tier-banner, [data-zelal-offer]')) {
          name = 'bundle_offer_viewed';
          facts = Object.assign(cartContext(element), {
            surface: element.matches('.product-tier-banner') ? 'product' : surfaceFor(element),
            offer_id: element.dataset.zelalOffer || 'band_volume_tiers', product_id: element.closest('product-info')?.dataset.productId
          });
        } else if (element.matches('[data-guide-section]')) {
          name = 'size_guide_section_viewed';
          facts = { surface: 'product', guide: 'band_fit', guide_section: element.dataset.guideSection, product_id: element.closest('product-info')?.dataset.productId };
        } else if (element.matches('[data-zelal-experiment]')) {
          name = 'experiment_exposed';
          facts = { experiment_id: element.dataset.zelalExperiment, experiment_variant: element.dataset.zelalVariant, surface: surfaceFor(element) };
        } else {
          var link = element.querySelector('a[href*="/products/"]');
          facts = link && productLinkFacts(link);
          if (!facts) return;
          name = 'product_recommendation_viewed';
        }
        key = name + ':' + JSON.stringify(facts);
        if (!seen.has(key)) { seen.add(key); publish(name, facts); }
        observer.unobserve(element);
      }, 500));
    });
  }, { threshold: 0.5 }) : null;

  function observeContent() {
    if (!observer) return;
    document.querySelectorAll('.product-tier-banner, [data-zelal-offer], [data-guide-section], product-recommendations .card-wrapper, [data-zelal-experiment]').forEach(function (element) {
      if (!observed.has(element)) { observed.add(element); observer.observe(element); }
    });
  }

  var scanQueued = false;
  new MutationObserver(function () {
    if (scanQueued) return;
    scanQueued = true;
    requestAnimationFrame(function () { scanQueued = false; observeContent(); });
  }).observe(document.documentElement, { childList: true, subtree: true });
  observeContent();

  function publishRenderedOutcomes() {
    document.querySelectorAll('[data-zelal-analytics-event]').forEach(function (element) {
      var name = element.dataset.zelalAnalyticsEvent;
      var surface = element.dataset.zelalAnalyticsSurface || 'storefront';
      publish(name, { surface: surface }, name + ':' + surface + ':' + window.location.pathname);
    });
  }

  document.addEventListener('zelal:welcome_subscribed', function () {
    publish('welcome_subscribed', { surface: 'welcome_popup' }, 'welcome_subscribed');
  });
  document.addEventListener('zelal:welcome_shown', function () {
    publish('welcome_shown', { surface: 'welcome_popup' });
  });
  document.addEventListener('zelal:welcome_dismissed', function () {
    publish('welcome_dismissed', { surface: 'welcome_popup' });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', publishRenderedOutcomes, { once: true });
  } else {
    publishRenderedOutcomes();
  }
})();
